import { Image as ImageJs } from 'image-js';
import CanvasImageRenderer from './canvasImageRenderer';
import { AdjustmentPresetType } from './PixijsFilters/AdjustmentFilterFactory';
import { loadImageFromURL } from './image-loader';
import { fabric } from 'fabric'
import { Size } from "@/shared/sync/mediaRepository/size";
import { Inset } from '@/shared/sync/mediaRepository/inset';
import { BasePixiFilter } from './PixijsFilters/BasePixiFilter';

type AutoAdjustProperty = 'exposure' | 'temperature' | 'tint'
type AutoAdjustValues = Record<AutoAdjustProperty, number>

/**
 * Auto-adjust output in editor "logic" (bzrt) units — the same units the Adjust
 * sliders read/write. Every key sits at its filter default when no correction is
 * warranted, so spreading this onto an object's adjustments is a no-op for a
 * well-balanced image.
 */
export type AutoAdjustResult = {
  exposure: number      // ExposureFilter, [-1.5, 1.5], default 0
  contrast: number      // ColorControlFilter, [0.75, 1.25], default 1.0
  vibrance: number      // VibranceFilter, [-1.2, 1.2], default 0
  temperature: number   // TemperatureFilter, [2000, 11000], default 6500
  tint: number          // TemperatureFilter, [-100, 100], default 0
}

interface ImageStats {
  lMean: number         // CIE L* mean (legacy enhance path)
  aMean: number         // CIE a* mean (white balance)
  bMean: number         // CIE b* mean (white balance)
  chromaMean: number    // mean Lab chroma (vibrance)
  lumaHistogram: number[] // sRGB-luma histogram (tone percentiles) — see worker
  count: number
}

// Thumbnail size used for statistical analysis. A 128px thumbnail is statistically
// equivalent to the full image for mean/percentile-based adjustments, but ~250×
// less work than a 2000px source.
const ANALYSIS_SIZE = 128

/** Tunables grouped by correction; `strength` scales everything toward identity. */
const TUNING = {
  /** Global correction strength (1 = full, 0 = no-op). */
  strength: 0.75,

  // Anchored levels → contrast + exposure. Percentiles are INNER, not p1/p99:
  // nearly every photo has a few near-0/255 pixels, so extreme points read as
  // "full range" and collapse the stretch to a no-op; inner points track the bulk.
  levelsBlackPct: 0.03,
  levelsWhitePct: 0.97,
  /** Max black/white-point pull-in (avoids crushing). */
  levelsBlackWhiteCap: 0.3,
  /** Softens the derived contrast toward identity. */
  contrastStrength: 0.8,
  /** Fade the stretch out as the median leaves mid-gray (high-/low-key → identity). */
  levelsCenterTolerance: 0.35,

  // Shadow lift → exposure. Brighten dark images toward a target, but only until
  // the near-white point (p98) reaches the ceil — so a spotlit subject on a dark
  // stage (misleading median) isn't over-brightened. "Expose to the highlights."
  liftTargetLuma: 0.45,
  liftStrength: 0.5,
  liftHighlightPct: 0.98,
  liftHighlightCeil: 0.7,
  /** Exposure clamps in stops (brighten more freely than darken). */
  maxExposureUp: 0.9,
  maxExposureDown: 0.5,

  // Gray-world → temperature / tint. Neutralize the average cast; back off on
  // strong casts (castGuard) since those are usually intentional color.
  wbStrength: 0.4,
  tempPerBStar: 30,
  tintPerAStar: 1.5,

  // Chroma gap → vibrance. Boost dull images; leave vivid or near-neutral alone.
  chromaTarget: 22,
  chromaFloorForVibrance: 3,
  maxVibrance: 0.25,
} as const

// Filter identity/range constants (see the individual *Filter.ts files).
// ColorControlFilter maps bzrt contrast [0.75,1.25] onto shader contrast [0.05,2].
const CONTRAST_MIN = 0.75
const CONTRAST_MAX = 1.25
const TEMP_DEFAULT = 6500
const TEMP_MIN = 2000
const TEMP_MAX = 11000
const TINT_MIN = -100
const TINT_MAX = 100

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value))

/**
 * Computes image auto-adjustments. Idea: analyze a downscaled thumbnail into
 * robust stats (an sRGB-luma histogram + mean Lab), derive the ideal tonal
 * mapping as a levels stretch — a linear input→output LUT that puts the black
 * point at 0 and the white point at 1 — then FIT that mapping onto the app's
 * existing sliders. Because the stretch is affine, it's reproduced exactly by an
 * (exposure, contrast) pair, so no custom LUT/curve filter is needed and it
 * renders through the normal pipeline. Color (white balance, vibrance) is derived
 * separately from the Lab means.
 */
export class AutoAdjustManager {

  private readonly rangeValueMap: Record<AutoAdjustProperty, { minimumValue: number; maximumValue: number }> = {
    exposure: { minimumValue: -0.5, maximumValue: 0.5 },
    temperature: { minimumValue: 4000, maximumValue: 7000 },
    tint: { minimumValue: -80, maximumValue: 80 },
  }

  // Persistent worker shared across all instances — avoids per-call creation overhead.
  private static _worker: Worker | null = null
  private static _pendingRequests = new Map<number, (result: ImageStats) => void>()
  private static _nextRequestId = 0

  private static getWorker(): Worker | null {
    if (typeof Worker === 'undefined') return null
    if (!AutoAdjustManager._worker) {
      try {
        AutoAdjustManager._worker = new Worker(
          new URL('./autoAdjustWorker.ts', import.meta.url)
        )
        AutoAdjustManager._worker.onmessage = (e: MessageEvent<{ id: number } & ImageStats>) => {
          const { id, lMean, aMean, bMean, chromaMean, lumaHistogram, count } = e.data
          AutoAdjustManager._pendingRequests.get(id)?.({ lMean, aMean, bMean, chromaMean, lumaHistogram, count })
          AutoAdjustManager._pendingRequests.delete(id)
        }
        AutoAdjustManager._worker.onerror = () => {
          AutoAdjustManager._worker = null
        }
      } catch {
        return null
      }
    }
    return AutoAdjustManager._worker
  }

  async adjust(base64: string): Promise<string> {
    const normalizedImageSource = this.normalizeImageSource(base64)
    const imgJs = await ImageJs.load(normalizedImageSource)
    const filters = await this.acquireFilters(imgJs)

    const imageElement: any = await loadImageFromURL(normalizedImageSource)
    const staticImage = new fabric.StaticImage(imageElement, {})
    staticImage.set('dirty', true)
    const adjustedStaticImage = staticImage.applyFilters(filters)

    // @ts-ignore
    const dataURL = adjustedStaticImage._element.toDataURL({
      format: 'png',
      quality: 1.0,
    })
    return dataURL
  }

  async computeAdjustments(base64: string): Promise<AutoAdjustResult> {
    const normalizedImageSource = this.normalizeImageSource(base64)
    const imgJs = await ImageJs.load(normalizedImageSource)
    const stats = await this.computeStats(this.downscale(imgJs))
    return this.deriveAdjustments(stats)
  }

  private normalizeImageSource = (imageSource: string): string => {
    if (
      imageSource.startsWith('data:') ||
      imageSource.startsWith('blob:') ||
      imageSource.startsWith('/') ||
      imageSource.startsWith('http://') ||
      imageSource.startsWith('https://')
    ) {
      return imageSource
    }
    return `data:${this.inferMimeType(imageSource)};base64,${imageSource}`
  }

  private inferMimeType = (rawBase64: string): string => {
    if (rawBase64.startsWith('/9j/')) return 'image/jpeg'
    if (rawBase64.startsWith('iVBORw0KGgo')) return 'image/png'
    if (rawBase64.startsWith('R0lGOD')) return 'image/gif'
    if (rawBase64.startsWith('UklGR')) return 'image/webp'
    return 'image/png'
  }

  // Downscale to a small thumbnail before analysis. Mean/percentile values are
  // statistically stable at low resolution, so this cuts the pixel loop from
  // millions to ~16K.
  private downscale(rgbaImage: ImageJs): ImageJs {
    return rgbaImage.width > ANALYSIS_SIZE || rgbaImage.height > ANALYSIS_SIZE
      ? rgbaImage.resize({ width: ANALYSIS_SIZE, preserveAspectRatio: true })
      : rgbaImage
  }

  /**
   * Turns the image stats into standard slider values: an anchored levels stretch
   * (→ contrast + exposure), a highlight-protected shadow lift (→ exposure), a
   * gray-world white balance (→ temperature/tint), and a chroma-gap vibrance boost.
   * A well-balanced image resolves to (near) identity.
   */
  private deriveAdjustments(stats: ImageStats): AutoAdjustResult {
    const identity: AutoAdjustResult = {
      exposure: 0,
      contrast: 1.0,
      vibrance: 0,
      temperature: TEMP_DEFAULT,
      tint: 0,
    }
    if (stats.count <= 0) return identity

    const { strength } = TUNING

    // Levels stretch y = (x-black)/(white-black), fit to the pipeline's
    // exposure(×2^e) → contrast((x-0.5)·c+0.5) by matching coefficients:
    //   contrast = 1 + 2·black/(white-black),  exposure = -log2(white+black).
    // The compensating exposure is what makes it a TRUE anchored stretch —
    // contrast alone (pivot 0.5) would blow out off-center images.
    const medNorm = this.percentile(stats, 0.5) / 255
    const loNorm = this.percentile(stats, TUNING.levelsBlackPct) / 255
    const hiNorm = this.percentile(stats, TUNING.levelsWhitePct) / 255

    // Shadow lift: brighten a dark image toward the target, capped so p98 (the
    // near-white point) can't exceed the ceil — protects already-bright content.
    let lift = 0
    if (medNorm < TUNING.liftTargetLuma) {
      const desired = Math.log2(TUNING.liftTargetLuma / Math.max(medNorm, 0.02)) * TUNING.liftStrength
      const pHiNorm = this.percentile(stats, TUNING.liftHighlightPct) / 255
      const maxLift = Math.max(0, Math.log2(TUNING.liftHighlightCeil / Math.max(pHiNorm, 0.01)))
      lift = clamp(desired, 0, maxLift)
    }

    // Contrast/levels are gated by the POST-lift median: a dark image lifted into
    // range earns contrast, while a genuinely high-key one stays ~identity.
    const medEffective = clamp(medNorm * Math.pow(2, lift), 0, 1)
    const centeredness = clamp(1 - Math.abs(medEffective - 0.5) / TUNING.levelsCenterTolerance, 0, 1)
    const spread = strength * centeredness
    const black = clamp(loNorm, 0, TUNING.levelsBlackWhiteCap) * spread
    const white = 1 - clamp(1 - hiNorm, 0, TUNING.levelsBlackWhiteCap) * spread

    const span = Math.max(0.05, white - black)
    const shaderContrastFull = 1 + (2 * black) / span
    const shaderContrast = 1 + (shaderContrastFull - 1) * TUNING.contrastStrength
    // Invert ColorControlFilter.normalize to express the shader contrast in bzrt units.
    const contrast = clamp(
      CONTRAST_MIN + ((shaderContrast - 0.05) / 1.95) * (CONTRAST_MAX - CONTRAST_MIN),
      CONTRAST_MIN,
      CONTRAST_MAX,
    )
    // Anchoring exposure for the stretch + the shadow lift.
    const exposure = clamp(-Math.log2(white + black) + lift, -TUNING.maxExposureDown, TUNING.maxExposureUp)

    // Gray-world white balance: neutralize the average cast (b*→temperature,
    // a*→tint). castGuard backs off on strong casts, which are usually intentional
    // color (a red product, a warm sunset) rather than a white-balance error.
    const aStar = stats.aMean - 128
    const bStar = stats.bMean - 128
    const castMag = Math.hypot(aStar, bStar)
    const castGuard = clamp(1 - (castMag - 10) / 40, 0.15, 1)
    const wb = TUNING.wbStrength * strength * castGuard
    const temperature = clamp(TEMP_DEFAULT - bStar * TUNING.tempPerBStar * wb, TEMP_MIN, TEMP_MAX)
    const tint = clamp(-aStar * TUNING.tintPerAStar * wb, TINT_MIN, TINT_MAX)

    // Vibrance: boost dull images; a near-neutral image has no color to boost.
    const dullness = clamp((TUNING.chromaTarget - stats.chromaMean) / TUNING.chromaTarget, 0, 1)
    const vibrance = stats.chromaMean < TUNING.chromaFloorForVibrance
      ? 0
      : clamp(dullness * TUNING.maxVibrance * strength, 0, TUNING.maxVibrance)

    return { exposure, contrast, vibrance, temperature, tint }
  }

  // Returns the L (0..255) at cumulative fraction `p` of the luma histogram.
  private percentile(stats: ImageStats, p: number): number {
    const target = p * stats.count
    let cumulative = 0
    for (let i = 0; i < 256; i++) {
      cumulative += stats.lumaHistogram[i]
      if (cumulative >= target) return i
    }
    return 255
  }

  // Legacy mean-based mapping, retained for the AI-Enhance apply path (`adjust`)
  // so its look is unchanged.
  private async extractAdjustments(rgbaImage: ImageJs): Promise<AutoAdjustValues> {
    const { lMean, aMean, bMean } = await this.computeStats(this.downscale(rgbaImage))
    return {
      exposure: this.labMeanToAdjustValue(lMean, 'exposure', 0.4, 0.6),
      tint: this.labMeanToAdjustValue(aMean, 'tint', 0.4, 0.6),
      temperature: this.labMeanToAdjustValue(bMean, 'temperature', 0.4, 0.6),
    }
  }

  // Offloads the pixel math to a persistent shared worker if available, falling
  // back to an inline single-pass loop so the code always works.
  private computeStats(img: ImageJs): Promise<ImageStats> {
    const worker = AutoAdjustManager.getWorker()
    if (worker) {
      return new Promise((resolve) => {
        const id = AutoAdjustManager._nextRequestId++
        AutoAdjustManager._pendingRequests.set(id, resolve)
        // Copy before transfer so img.data remains intact on the main thread.
        const pixels = img.data.slice(0) as Uint8Array
        worker.postMessage({ id, pixels, channels: img.channels }, [pixels.buffer])
      })
    }
    return Promise.resolve(this.computeStatsInline(img))
  }

  // Inline fallback: single-pass RGBA→Lab stats, skipping near-transparent pixels.
  // Kept in lockstep with autoAdjustWorker.ts.
  private computeStatsInline(img: ImageJs): ImageStats {
    const { data, channels } = img
    const hasAlpha = channels === 4
    const lumaHistogram = new Array(256).fill(0)
    let sumL = 0, sumA = 0, sumB = 0, sumChroma = 0, count = 0

    for (let i = 0; i < data.length; i += channels) {
      if (hasAlpha && data[i + 3] < 16) continue
      const r = data[i], g = data[i + 1], b = data[i + 2]
      const [lNorm, aNorm, bNorm] = this.rgbToLabNorm(r, g, b)
      sumL += lNorm
      sumA += aNorm
      sumB += bNorm
      const aStar = aNorm - 128, bStar = bNorm - 128
      sumChroma += Math.sqrt(aStar * aStar + bStar * bStar)
      // sRGB luma (matches the contrast shader's luma weights). See worker.
      const luma = 0.2125 * r + 0.7154 * g + 0.0721 * b
      lumaHistogram[Math.min(255, luma | 0)]++
      count++
    }

    return {
      lMean: count > 0 ? sumL / count : 128,
      aMean: count > 0 ? sumA / count : 128,
      bMean: count > 0 ? sumB / count : 128,
      chromaMean: count > 0 ? sumChroma / count : 0,
      lumaHistogram,
      count,
    }
  }

  // Returns normalized [0, 255] L, A, B for a single RGB pixel.
  private rgbToLabNorm = (r: number, g: number, b: number): [number, number, number] => {
    let rr = r / 255, gg = g / 255, bb = b / 255
    rr = rr > 0.04045 ? Math.pow((rr + 0.055) / 1.055, 2.4) : rr / 12.92
    gg = gg > 0.04045 ? Math.pow((gg + 0.055) / 1.055, 2.4) : gg / 12.92
    bb = bb > 0.04045 ? Math.pow((bb + 0.055) / 1.055, 2.4) : bb / 12.92

    const x = (rr * 0.4124 + gg * 0.3576 + bb * 0.1805) * 100
    const y = (rr * 0.2126 + gg * 0.7152 + bb * 0.0722) * 100
    const z = (rr * 0.0193 + gg * 0.1192 + bb * 0.9505) * 100

    let fx = x / 95.047, fy = y / 100.0, fz = z / 108.883
    fx = fx > 0.008856 ? Math.cbrt(fx) : 7.787 * fx + 16 / 116
    fy = fy > 0.008856 ? Math.cbrt(fy) : 7.787 * fy + 16 / 116
    fz = fz > 0.008856 ? Math.cbrt(fz) : 7.787 * fz + 16 / 116

    const l = 116 * fy - 16
    const a = 500 * (fx - fy)
    const labB = 200 * (fy - fz)

    return [
      Math.min(255, Math.max(0, (l / 100) * 255)),
      Math.min(255, Math.max(0, a + 128)),
      Math.min(255, Math.max(0, labB + 128)),
    ]
  }

  private labMeanToAdjustValue = (
    meanValue: number,
    property: AutoAdjustProperty,
    min: number,
    max: number,
  ): number => {
    const meanPercent = meanValue / 255
    const capped = Math.min(Math.max(meanPercent, min), max)
    const { minimumValue, maximumValue } = this.rangeValueMap[property]
    const limitedCapped = Math.max(Math.min(1.0, capped), 0.0)
    // The original getValueInRange was called with minimumValue/maximumValue
    // passed into swapped parameter slots, which inverts the mapping: a high
    // channel mean produces a value toward the range minimum (a compensating
    // curve — e.g. a bright image darkens). Preserve that inverted behavior.
    return maximumValue + limitedCapped * (minimumValue - maximumValue)
  }

  private async acquireFilters(rgbaImage: ImageJs): Promise<any[]> {
    const { exposure, tint, temperature } = await this.extractAdjustments(rgbaImage)
    const adjustmentFilterFactory = CanvasImageRenderer.getInstance().adjustmentFilterFactory
    const exposureFilter = adjustmentFilterFactory.getFilter(AdjustmentPresetType.exposure)({ exposure })
    const tintFilter = adjustmentFilterFactory.getFilter(AdjustmentPresetType.temperatureTint)({ temperature, tint })
    return [exposureFilter, tintFilter].map(
      (f) => new BasePixiFilter(f, new Size(0, 0), new Inset(0, 0, 0, 0))
    )
  }
}
